import { Maybe, Money, PayOverTimeBanner } from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import { mapAlertType } from 'features/lineOfCredit/screens/activeDetailsScreen/helpers/activeDetailsScreen.helpers';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import Button from 'shared/components/vxl/Button/Button';
import HairLine from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { TagType } from 'shared/components/vxl/Tag/Tag';
import ExpressiveSmText from 'shared/components/vxl/typography/expressive/ExpressiveSmText';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

export interface LoanViewHeaderProps {
  banner: Maybe<PayOverTimeBanner | null>;
  dueDate: Maybe<string | null>;
  remainingBalance: Maybe<Money | null>;
  paymentDue: Maybe<Money | null>;
  isAutoPayEnabled: boolean;
  loanId: string;
}

const LoanViewHeader = ({
  banner,
  dueDate,
  isAutoPayEnabled,
  remainingBalance,
  paymentDue,
  loanId,
}: LoanViewHeaderProps) => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('payOverTime', {
    keyPrefix: 'loanDetails',
  });

  const handleMakeAPaymentAction = () => {
    router.push({
      pathname: '/pay-over-time/manual-payment',
      params: {
        autoPayEnabled: isAutoPayEnabled.toString(),
        loanId,
      },
    });
  };

  const handlePaymentScheduleAction = () => {
    router.push({
      pathname: '/pay-over-time/payment-schedule',
      params: {
        loanId,
      },
    });
  };

  const handleAutopayAction = () => {
    router.push({
      pathname: '/pay-over-time/autopay',
      params: {
        autoPayEnabled: isAutoPayEnabled.toString(),
        loanId,
      },
    });
  };

  return (
    <View>
      {banner ? (
        <View style={styles.bannerWrapper}>
          <InlineAlert
            titleText={banner.title}
            bodyText={banner.message}
            alertType={mapAlertType(banner.bannerStatus)}
          />
        </View>
      ) : null}
      <View style={styles.balanceContainer}>
        <BodySmBoldText data-testId="amountDueTitle" numberOfLines={0}>
          {translatedText('amountDue')}
        </BodySmBoldText>
        {paymentDue?.formattedWithPlusPrefix ? (
          <ExpressiveSmText data-testId="amount" numberOfLines={0}>
            {paymentDue.formattedWithPlusPrefix}
          </ExpressiveSmText>
        ) : null}
      </View>
      <ListItem
        primaryCopy={translatedText('nextPaymentDue')}
        showsChevron={false}
        trailingAccessory={{
          id: 'nextPaymentDue',
          name: VXLComponentId.LIST_ITEM_TEXT_STACK,
          props: {
            primaryCopy: dueDate || '',
          },
        }}
      />
      <View style={styles.buttonWrapper}>
        <Button
          label={translatedText('makePayment')}
          onPress={handleMakeAPaymentAction}
          analyticsId="makePaymentClicked"
          customAttributes={{
            cta: 'Make a payment',
            account_type: 'payOverTime',
          }}
        />
      </View>
      <View style={styles.sectionDivider} />
      <ListItem
        primaryCopy={translatedText('remainingBalance')}
        showsChevron={false}
        trailingAccessory={{
          id: 'remainingBalance',
          name: VXLComponentId.LIST_ITEM_TEXT_STACK,
          props: {
            primaryCopy: remainingBalance?.formattedWithPlusPrefix || '',
          },
        }}
      />
      <View style={styles.hairlineWrapper}>
        <HairLine />
      </View>
      <ListItem
        primaryCopy={translatedText('autopay')}
        primaryNumberOfLines={1}
        showsChevron
        onPress={handleAutopayAction}
        trailingAccessory={{
          id: 'autopayStatus',
          name: VXLComponentId.TAG,
          props: {
            text: translatedText(
              isAutoPayEnabled ? 'autoPayStatus.on' : 'autoPayStatus.off',
            ),
            type: isAutoPayEnabled ? TagType.SUCCESS : TagType.ERROR,
          },
        }}
      />
      <View style={styles.hairlineWrapper}>
        <HairLine />
      </View>
      <ListItem
        primaryCopy={translatedText('paymentSchedule')}
        showsChevron
        onPress={handlePaymentScheduleAction}
      />
      <View style={styles.sectionDivider} />
    </View>
  );
};

const styles = StyleSheet.create({
  balanceContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    gap: 4,
    margin: theme.spacing.m,
  },
  bannerWrapper: {
    margin: theme.spacing.m,
  },
  buttonWrapper: {
    marginBottom: theme.spacing.m,
    marginHorizontal: theme.spacing.m,
    marginTop: theme.spacing.s,
  },
  hairlineWrapper: {
    marginHorizontal: theme.spacing.m,
  },
  sectionDivider: {
    backgroundColor: theme.color.background.surface,
    height: theme.spacing.s,
  },
});

export default LoanViewHeader;
